import moment from "moment";

// Analytics API field name constants
const ANALYTICS_FIELDS = {
  INCIDENT_TYPE: "Incident Type",
  RECORD_COUNT: "Record Count",
} as const;

export type IncidentTypeOption = {
  label: string;
  value: string;
  meta?: any;
};

export const ALL_INCIDENTS_OPTION = {
  label: "All Incidents",
  value: "all_incidents",
} as const;

export const normalizeIncidentTypeInput = (
  input: string | string[] | null | undefined
): string[] => {
  if (!input) return [];

  const toArray = Array.isArray(input) ? input : [input];

  const normalized = toArray
    .flatMap((value) =>
      String(value)
        .split(",")
        .map((item) => item.trim())
    )
    .filter(Boolean);

  return Array.from(new Set(normalized));
};

export const mapIncidentTypeOptions = (
  input: any,
  { sort = true }: { sort?: boolean } = {}
): IncidentTypeOption[] => {
  const source = Array.isArray(input) ? input : input?.data?.data;

  if (!Array.isArray(source)) return [];

  const mapped = source
    .map((item: any) => {
      const label = item?.Name ?? item?.label ?? "";
      const value = item?.Id ?? item?.value ?? item?.id ?? "";

      if (!label || !value) return null;

      return {
        label,
        value,
        meta: item,
      } as IncidentTypeOption;
    })
    .filter(Boolean) as IncidentTypeOption[];

  if (!sort) return mapped;

  return mapped.sort((a, b) => a.label.localeCompare(b.label));
};

export const resolveIncidentTypeSelection = (
  input: string | string[] | null | undefined,
  options: IncidentTypeOption[],
  {
    allToken = ALL_INCIDENTS_OPTION.label.toLowerCase(),
  }: { allToken?: string } = {}
) => {
  const normalized = normalizeIncidentTypeInput(input);

  const optionByLabel = new Map(
    options.map((option) => [option.label.toLowerCase(), option])
  );
  const optionByValue = new Map(
    options.map((option) => [String(option.value).toLowerCase(), option])
  );

  let hasAllToken = false;
  const derivedLabels: string[] = [];

  normalized.forEach((entry) => {
    const key = entry.toLowerCase();

    if (key === allToken) {
      hasAllToken = true;
      return;
    }

    const option = optionByLabel.get(key) ?? optionByValue.get(key);

    if (option) {
      derivedLabels.push(option.label);
      return;
    }

    derivedLabels.push(entry);
  });

  const labels = hasAllToken
    ? options.map((option) => option.label)
    : Array.from(new Set(derivedLabels));

  const selectedOptionMap = new Set(labels);
  const matchingOptions = options.filter((option) =>
    selectedOptionMap.has(option.label)
  );

  const isAllSelected =
    hasAllToken ||
    (options.length > 0 && matchingOptions.length === options.length);

  return {
    labels,
    matchingOptions,
    isAllTokenSelected: hasAllToken,
    isAllSelected,
  };
};

export const getIncidentListInitialValues = () => {
  return { facilityIds: [""], incidentTypes: [""], startDate: "", endDate: "" };
};

export const getRepeatIncidentInitialValues = () => {
  return {
    facilityIds: [""],
    incidentTypes: [],
    residentStatus: [],
    startDate: "",
    endDate: "",
  };
};

export const getStatusReportInitialValues = () => {
  return { facilityIds: [""], startDate: "", endDate: "" };
};

export const getIncidentTrendInitialValues = () => {
  return {
    facilityIds: "",
    incidentTypes: "",
    startDate: "",
    endDate: "",
  };
};

export const getWoundAnalysisInitialValues = () => {
  return {
    facilityIds: [""],
    skinIssueTypes: [],
    startDate: "",
    endDate: "",
  };
};

export const getBehaviorAnalysisInitialValues = () => {
  return {
    facilityIds: [""],
    startDate: "",
    endDate: "",
    behaviorIncidentTypes: [],
    behaviorIncidentSubTypes: [],
  };
};

export const getIncidentRateInitialValues = () => {
  return {
    facilityIds: [""],
    incidentTypes: [],
    residentStatus: [],
    startDate: "",
    endDate: "",
  };
};

export const getInjuryReportInitialValues = () => {
  return {
    facilityIds: [""],
    incidentTypes: [],
    injuryTypes: [],
    startDate: "",
    endDate: "",
  };
};

export const getFilteredIncident = (data: any) => {
  const _data = data.filter(
    (item: any) => !item.monthAndYear?.includes("null")
  );

  _data.sort(function (a: any, b: any) {
    const [monthA, yearA] = a.monthAndYear.split(" ").map(Number);
    const [monthB, yearB] = b.monthAndYear.split(" ").map(Number);

    if (yearA !== yearB) return yearA - yearB;
    else return monthA - monthB;
  });

  return _data;
};

export const getUniqueIncidentTypes = (data: any) => {
  return Array.from(
    new Set(
      data.flatMap((item: any) => {
        return item.incidentReports.map(
          (report: any) => report[ANALYTICS_FIELDS.INCIDENT_TYPE]
        );
      })
    )
  );
};

export const extractDateString = (dateString: string) => {
  const dateFormats = ["MMM YYYY", "MM/YYYY", "YYYY-MMM", "MMM-YY"];
  const date = moment(dateString, dateFormats);
  const month = date.month() + 1;
  const year = date.year();

  return { month, year };
};

export const getMonthDateRange = (monthName: string, year: number) => {
  const monthIndex = new Date(Date.parse(monthName + " 1, " + year)).getMonth();

  const startDate = new Date(year, monthIndex, 1);

  const endDate = new Date(year, monthIndex + 1, 0);

  return {
    start: moment(startDate).format("YYYY-MM-DD"),
    end: moment(endDate).format("YYYY-MM-DD"),
  };
};

export const transformRepeatIncidentsForGrid = (data: any[] | null) => {
  if (!Array.isArray(data)) return [];

  return data
    .map((facility) => {
      const residents = (facility?.info || [])
        .map((resident: any) => {
          const incidents = Array.isArray(resident?.Incidents)
            ? resident.Incidents
            : [];

          // Use the backend-provided count instead of calculating length
          const incidentCount = resident?.NumberOfIncident || incidents.length;
          const residentStatus = resident?.ResidentStatus || "";
          const residentNameWithStatus = residentStatus
            ? `${resident?.ResidentName || "-"} (${residentStatus})`
            : resident?.ResidentName || "-";

          const transformedIncidents = incidents.map((incident: any) => {
            const incidentId =
              incident?.IncidentID || incident?.IncidentId || "-";

            return {
              FacilityName: facility?.FacilityName || "-",
              ResidentName: residentNameWithStatus,
              IncidentCount: incidentCount,
              incidentId: incidentId,
              IncidentName: incident?.IncidentName || "-",
              IncidentType: incident?.IncidentType || "-",
              FallType: incident?.FallType || "-",
              // FacilityDate/FacilityTime are the values the nurse entered, as-is
              FacilityDate: incident?.FacilityDate,
              FacilityTime: incident?.FacilityTime,
              DayOfTheWeek: incident?.DayOfTheWeek,
              Injuries: incident?.Injuries,
              SentToAcuteCareFacility: incident?.SentToAcuteCareFacility,
              Location: incident?.Place || "-",
              PCCUnitDescription: incident?.PCCUnitDescription || "-",
            };
          });
          // Removed .sort() since backend already sorts the data

          return {
            residentName: residentNameWithStatus,
            incidentCount,
            incidents: transformedIncidents,
          };
        })
        .filter(Boolean);

      if (!residents.length) return null;

      return {
        facilityName: facility?.FacilityName || "-",
        residents,
      };
    })
    .filter(Boolean);
};

export const getTableCols = (data: any, grouping: any) => {
  if (!data.length) return [];

  
  let dynamicWidth = {};
  if (data.length > 15) {
    dynamicWidth = { width: "80px" };
  }

  // Support both API formats: periodStartDate (new) and monthAndYear (legacy)
  const uniquePeriods = Array.from(
    new Set(data.map((item: any) => item.periodStartDate ?? item.monthAndYear))
  );

  const tableCols = [
    {
      Header: "Incident Type",
      accessor: "incidentType",
      width: "140px",
      Cell: ({ value }: any) => (value?.length ? value : "Total"),
    },
    ...uniquePeriods.map((period: any, index: number) => {
      let columnHeader = "";

      if (moment(period).isValid()) {
        switch(grouping) {
          case "Weekly":
            columnHeader = moment(period).format("l");
            break;
          case "Monthly":
            columnHeader = moment(period).format("MMM YYYY");
            break;
          case "Quarterly":
            columnHeader = moment(period).format('[Q]Q YYYY');
            break;
          case "Yearly":
            columnHeader = moment(period).format("YYYY");
            break;    
        }
      } else {
        columnHeader = period;  
      }

      let prevPeriod = undefined;
      if (index > 0) {
          prevPeriod = uniquePeriods[index - 1] as string;         
      }

      return {
        Header: columnHeader,
        accessor: period,
        customCellType: "trend",
        previousMonthField: prevPeriod,
        ...dynamicWidth,
        Cell: ({ value }: any) => value || 0,
      };
    }),
    {
      Header: "Total",
      accessor: "total",
      width: "80px",
      Cell: ({ value }: any) => value || 0,
    },
  ];

  return tableCols;
};

export const getTableData = (types: any, data: any, grouping: any) => {
  return types.map((incidentType: any) => {
    const row: Record<string, any> = { incidentType };
    data.forEach((item: any) => {
      // Support both API formats: periodStartDate (new) and monthAndYear (legacy)
      const accessor = item.periodStartDate ?? item.monthAndYear;   
      const record = item.incidentReports.find(
        (report: any) => report[ANALYTICS_FIELDS.INCIDENT_TYPE] === incidentType
      );
      row[accessor] = record
        ? record[ANALYTICS_FIELDS.RECORD_COUNT]
        : 0;
    });
    return row;
  });
};

export const addTotal = (data: any) => {
  return data.map((object: any) => {
    const total = Object.values(object)
      .filter((value) => typeof value === "number")
      .reduce((acc: any, value: any) => acc + value, 0);
    return { ...object, total };
  });
};

export const convertDataForLineChart = (data: any) => {
  if (!data || data.length === 0) {
    return null;
  }

  const labels = Object.keys(data[0]).filter((key) => key !== "incidentType");
  const datasets: any = [];
  const incidentTypes: any = [];

  data.forEach((item: any) => {
    const incidentType = item.incidentType;
    if (!incidentTypes.includes(incidentType)) {
      const dataValues = labels.map((label) => item[label]) || 0;
      datasets.push({
        label: incidentType,
        data: dataValues,
        fill: false,
        borderColor: getRandomColor(),
        pointBorderWidth: 5,
        pointRadius: 10,
        pointHoverRadius: 15,
        tension: 0.4,
        pointHoverBorderWidth: 5,
      });
      incidentTypes.push(incidentType);
    }
  });

  return {
    labels: labels,
    datasets: datasets,
  };
};

export const getRandomColor = (() => {
  let hue = 0;

  return () => {
    const color = `hsl(${hue}, 70%, 50%)`;
    hue = (hue + 37) % 360;
    return color;
  };
})();

export const modifyTitle = (title: any) => {
  if (!title) return "";
  const words = title.split("-");
  const capitalize = words.map(
    (word: any) => word.charAt(0).toUpperCase() + word.slice(1)
  );
  return capitalize.join(" ");
};

export const sortIncidentType = (a: any, b: any) => {
  if (
    (a.incidentType === null && b.incidentType !== null) ||
    (a.incidentType === "Total" && b.incidentType !== "Total")
  ) {
    return 1;
  }
  if (
    (a.incidentType !== null && b.incidentType === null) ||
    (a.incidentType !== "Total" && b.incidentType === "Total")
  ) {
    return -1;
  }
  return 0;
};

/**
 * Flattens injury report data so that each entry in an incident's Injuries array
 * becomes its own grid row, with the parent incident fields (IncidentName, FacilityDate,
 * FacilityTime, IncidentType, IncidentLocation, SentToAcuteCareFacility) carried over to
 * every child row. Incidents with no injuries produce a single placeholder row with "-"
 * for all injury-specific fields.
 * @param data - Array of facilities with incidents containing a nested Injuries array
 * @returns Facility array where each incident is expanded to one row per injury
 */
export const transformInjuryReportData = (data: any[] | null) => {
  if (!Array.isArray(data)) return [];

  return data.map((facility) => ({
    ...facility,
    incidents:
      facility?.incidents?.flatMap((incident: any) => {
        const injuries = incident?.Injuries || [];

        // If the incident has no injuries, still show one row with empty injury fields
        if (injuries.length === 0) {
          return [
            {
              ...incident,
              InjuryType: "-",
              InjuryArea: "-",
              InjuryLocation: "-",
              InjurySide: "-",
              InjuryDescription: "-",
            },
          ];
        }

        // One row per injury, carrying over the parent incident fields
        return injuries.map((injury: any) => ({
          ...incident,
          InjuryType: injury?.InjuryType || "-",
          InjuryArea: injury?.InjuryArea || "-",
          InjuryLocation: injury?.Location || "-",
          InjurySide: injury?.InjurySide || "-",
          InjuryDescription: injury?.InjuryDescription || "-",
        }));
      }) || [],
  }));
};
